/**
 * Wakes the service worker and, when the worker supports it, waits for an
 * acknowledgement that its script is loaded and listening.
 *
 * Why: BroadcastChannel messages do NOT start a stopped service worker. When
 * the browser has terminated the worker (idle, laptop sleep, background tab),
 * a request sent over a channel is lost and waits until its timeout. ServiceWorker.postMessage() does start the worker ("message"
 * event).
 *
 * The worker side of the handshake lives in src/sw.ts.
 */
import {
  readServiceWorkerPongDetails,
  ServiceWorkerPongDetails,
  ServiceWorkerWakeMessageType,
} from 'shared/domain/messages/serviceWorkerWake';

export enum SwWakeResult {
  /** No service worker API (e.g. unsupported browser). */
  unsupported = 'unsupported',
  /** No registration / active worker. */
  noWorker = 'no-worker',
  /** The worker acknowledged a ping a moment ago. */
  cached = 'cached',
  /** The worker answered the ping. */
  alive = 'alive',
  /** Wake-up sent without an acknowledgement (e.g. an old sw.js without the handler). */
  wokenNoAck = 'woken-no-ack',
  /** The service worker API threw (e.g. private mode) – the request is sent anyway. */
  error = 'error',
  /** Waking was not attempted. */
  skipped = 'skipped',
}

/** Whether the worker answered a ping (now or a moment ago). */
export function isServiceWorkerAwake(result: unknown): boolean {
  return result === SwWakeResult.alive || result === SwWakeResult.cached;
}

/** How long a successful ping is trusted. */
const ALIVE_CACHE_MS = 5_000;
/** When the worker does not answer pings (old sw.js), do not wait for an ack for this long. */
const NO_ACK_BACKOFF_MS = 60_000;
/** Caches the "no worker" result, so not every call waits for it. */
const NO_WORKER_CACHE_MS = 30_000;

let lastAliveAt = 0;
let lastPongDetails: ServiceWorkerPongDetails | undefined;
let noAckUntil = 0;
let noWorkerUntil = 0;
let inFlight: Promise<SwWakeResult> | null = null;

function getContainer(): ServiceWorkerContainer | undefined {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return undefined;
  }
  return navigator.serviceWorker;
}

export function getCurrentController(): ServiceWorker | null {
  return getContainer()?.controller ?? null;
}

function delay(ms: number): Promise<null> {
  return new Promise((resolve) => setTimeout(() => resolve(null), ms));
}

/** The active worker, also when it does not control the page yet (e.g. after a hard reload). */
export async function resolveServiceWorker(
  container: ServiceWorkerContainer,
  readyTimeoutMs: number
): Promise<ServiceWorker | null> {
  if (container.controller) {
    return container.controller;
  }

  // After a hard reload (Shift+F5) the page has no controller, but the worker is active.
  const registration = await container.getRegistration();
  if (!registration) {
    return null;
  }
  if (registration.active) {
    return registration.active;
  }

  // The worker is installing / activating – wait a limited time.
  const ready = await Promise.race([container.ready, delay(readyTimeoutMs)]);
  return ready?.active ?? null;
}

function ping(worker: ServiceWorker, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const finish = (result: boolean): void => {
      clearTimeout(timer);
      channel.port1.onmessage = null;
      channel.port1.close();
      resolve(result);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);

    channel.port1.onmessage = (event: MessageEvent): void => {
      // A pong without details (an older sw.js) is another instance: none of the last one's details stay.
      lastPongDetails = readServiceWorkerPongDetails(event.data);
      finish(true);
    };

    try {
      worker.postMessage({ type: ServiceWorkerWakeMessageType.ping }, [
        channel.port2,
      ]);
    } catch {
      finish(false);
    }
  });
}

export function ensureServiceWorkerAwake({
  pingTimeoutMs = 2_500,
  readyTimeoutMs = 3_000,
}: { pingTimeoutMs?: number; readyTimeoutMs?: number } = {}): Promise<SwWakeResult> {
  const container = getContainer();
  if (!container) {
    return Promise.resolve(SwWakeResult.unsupported);
  }

  const now = Date.now();
  if (now - lastAliveAt < ALIVE_CACHE_MS) {
    return Promise.resolve(SwWakeResult.cached);
  }
  if (now < noWorkerUntil) {
    return Promise.resolve(SwWakeResult.noWorker);
  }

  // A resync fires a dozen entities at once – share one ping instead of 15.
  if (inFlight) {
    return inFlight;
  }

  // Waking is best-effort: it must NEVER reject, that would block the request.
  inFlight = (async (): Promise<SwWakeResult> => {
    let worker: ServiceWorker | null;
    try {
      worker = await resolveServiceWorker(container, readyTimeoutMs);
    } catch {
      noWorkerUntil = Date.now() + NO_WORKER_CACHE_MS;
      return SwWakeResult.error;
    }
    if (!worker) {
      noWorkerUntil = Date.now() + NO_WORKER_CACHE_MS;
      return SwWakeResult.noWorker;
    }

    if (Date.now() < noAckUntil) {
      // Old sw.js without the ping handler: postMessage alone still wakes it.
      try {
        worker.postMessage({ type: ServiceWorkerWakeMessageType.ping });
      } catch {
        // ignore
      }
      return SwWakeResult.wokenNoAck;
    }

    const acked = await ping(worker, pingTimeoutMs);
    if (acked) {
      lastAliveAt = Date.now();
      noAckUntil = 0;
      return SwWakeResult.alive;
    }

    noAckUntil = Date.now() + NO_ACK_BACKOFF_MS;
    return SwWakeResult.wokenNoAck;
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

/**
 * The worker instance that answered the last ping (start time, version);
 * `undefined` before a pong with details, or once the worker may have changed.
 */
export function getLastServiceWorkerPong(): ServiceWorkerPongDetails | undefined {
  return lastPongDetails;
}

/**
 * Forces a fresh wake-up on the next call (e.g. after a timeout): drops the
 * cached "alive", "no worker" and "no ack" results, since the worker may have
 * been stopped, registered or updated to a version that answers pings. The
 * instance that answered last is dropped too: the next pong tells the current one.
 */
export function markServiceWorkerStale(): void {
  lastAliveAt = 0;
  noWorkerUntil = 0;
  noAckUntil = 0;
  lastPongDetails = undefined;
}

